import { DEFAULT_LOGIN_CREDIT_LINE, type Directory } from './directory';
import type { Manifest, ManifestModule } from './manifest';
import { defaultGrantCategoriesFor, findMiddlewareModule, MIDDLEWARE_MODULES } from './middlewares';
import { MODULE_CATALOGUE, modulePermission } from './modules';
import { ODOO_MODULE_IDS } from './odoo-timeoff';
import { SEEDED_ROLES } from './roles';

/**
 * Example payloads for two fictional schools (ASB, NFA). Used by unit tests, the mobile
 * app's mock mode and dev seeds. Hosts use the reserved example domain, never real ones.
 */
export const FIXTURE_ROOT = 'teachpartner.example.com';

function modulesFor(enabledIds: readonly string[]): ManifestModule[] {
  return MODULE_CATALOGUE.map((m, index) => ({
    id: m.id,
    enabled: enabledIds.includes(m.id),
    title: m.title,
    icon: m.icon,
    permission: modulePermission(m.id),
    order: index + 1,
    config: {},
  }));
}

export const fixtureDirectory: Directory = {
  apiVersion: '1',
  minAppVersion: '1.0.0',
  tenants: [
    {
      code: 'ASB',
      name: 'Ashbury School',
      tenantUrl: `https://asb.${FIXTURE_ROOT}`,
      logoUrl: null,
      status: 'active',
    },
    {
      code: 'NFA',
      name: 'Northfield Academy',
      tenantUrl: `https://nfa.${FIXTURE_ROOT}`,
      logoUrl: null,
      status: 'maintenance',
    },
    {
      code: 'OLD',
      name: 'Closed School',
      tenantUrl: `https://old.${FIXTURE_ROOT}`,
      logoUrl: null,
      status: 'disabled',
    },
  ],
  app: { creditLine: DEFAULT_LOGIN_CREDIT_LINE },
};

export const fixtureManifestAsb: Manifest = {
  apiVersion: '1',
  minAppVersion: '1.0.0',
  tenant: {
    code: 'ASB',
    name: 'Ashbury School',
    shortName: 'ASB',
    logoUrl: '/api/v1/branding/logo',
    status: 'active',
    theme: {
      light: {
        primary: '#0B3D91',
        onPrimary: '#FFFFFF',
        accent: '#F2A900',
        background: '#FFFFFF',
        surface: '#F5F7FA',
        text: '#111827',
      },
      dark: {
        primary: '#4C7DD9',
        onPrimary: '#FFFFFF',
        accent: '#F2C14E',
        background: '#0B1220',
        surface: '#111A2E',
        text: '#E5E7EB',
      },
    },
  },
  auth: {
    issuer: `https://asb.${FIXTURE_ROOT}/realms/teachpartner`,
    clientId: 'teachpartner-app',
    providers: [
      { id: 'google', label: 'Continue with Google', kcIdpHint: 'google' },
      { id: 'microsoft', label: 'Continue with Microsoft', kcIdpHint: 'microsoft' },
      { id: 'emailOtp', label: 'Continue with email', kcIdpHint: null },
    ],
  },
  features: { biometricUnlockAllowed: true, lockAfterBackgroundSeconds: 300, pushEnabled: true },
  modules: modulesFor(MODULE_CATALOGUE.map((m) => m.id)),
  support: { email: 'it@ashbury.example', url: null },
};

export const fixtureManifestNfa: Manifest = {
  ...fixtureManifestAsb,
  tenant: {
    code: 'NFA',
    name: 'Northfield Academy',
    shortName: 'Northfield',
    logoUrl: '/api/v1/branding/logo',
    status: 'active',
    theme: {
      light: {
        primary: '#1F6B4A',
        onPrimary: '#FFFFFF',
        accent: '#E4572E',
        background: '#FFFFFF',
        surface: '#F3F7F4',
        text: '#14231B',
      },
      dark: {
        primary: '#3FA77A',
        onPrimary: '#0B1A12',
        accent: '#F07A57',
        background: '#0C1511',
        surface: '#14231B',
        text: '#E6EFE9',
      },
    },
  },
  auth: {
    issuer: `https://nfa.${FIXTURE_ROOT}/realms/teachpartner`,
    clientId: 'teachpartner-app',
    providers: [
      { id: 'microsoft', label: 'Continue with Microsoft', kcIdpHint: 'microsoft' },
      { id: 'emailOtp', label: 'Continue with email', kcIdpHint: null },
    ],
  },
  modules: modulesFor(['announcements', 'timetable']),
  support: { email: 'it@northfield.example', url: null },
};

/**
 * App mock mode and the web E2E mock tier: ASB also has the PowerSchool · Timetable plug-in
 * module switched on, and the school granted it to Teachers (no seeded role gets it by default).
 * One definition so the mock backend and the E2E expectations can't drift apart.
 */
const POWERSCHOOL_TIMETABLE_FIXTURE = MIDDLEWARE_MODULES.find(
  (m) => m.id === 'powerschool.timetable',
);
if (!POWERSCHOOL_TIMETABLE_FIXTURE)
  throw new Error('powerschool.timetable missing from MIDDLEWARE_MODULES');

export const fixtureMiddlewareModuleAsb: ManifestModule = {
  id: POWERSCHOOL_TIMETABLE_FIXTURE.id,
  enabled: true,
  title: POWERSCHOOL_TIMETABLE_FIXTURE.fullTitle,
  icon: POWERSCHOOL_TIMETABLE_FIXTURE.icon,
  permission: POWERSCHOOL_TIMETABLE_FIXTURE.permission,
  order: fixtureManifestAsb.modules.length + 1,
  config: {},
};

/**
 * The three Odoo Time Off tiles at the mock school ASB (the one mock school with Odoo allowed),
 * after PowerSchool · Timetable, all switched on (docs/plans/odoo-timeoff.md §2).
 */
export const fixtureOdooModulesAsb: readonly ManifestModule[] = ODOO_MODULE_IDS.map((id, i) => {
  const entry = findMiddlewareModule(id);
  if (!entry) throw new Error(`${id} missing from MIDDLEWARE_MODULES`);
  return {
    id,
    enabled: true,
    title: entry.fullTitle,
    icon: entry.icon,
    permission: modulePermission(id),
    order: fixtureMiddlewareModuleAsb.order + 1 + i,
    config: {},
  };
});

export const fixtureMockManifestAsb: Manifest = {
  ...fixtureManifestAsb,
  modules: [...fixtureManifestAsb.modules, fixtureMiddlewareModuleAsb, ...fixtureOdooModulesAsb],
};

/**
 * Extra permissions the mock school grants per seeded role id: PowerSchool · Timetable to
 * Teachers, and each Odoo tile's catalogue `defaultGrant` (the DEMO seed does the same). The
 * Tenant Admin holds everything anyway.
 */
export const FIXTURE_MOCK_ROLE_GRANTS: Readonly<Record<string, readonly string[]>> =
  Object.fromEntries(
    SEEDED_ROLES.filter((r) => r.id !== 'tenant_admin').map((r) => [
      r.id,
      [
        ...(r.id === 'teacher' ? [POWERSCHOOL_TIMETABLE_FIXTURE.permission] : []),
        ...ODOO_MODULE_IDS.filter((id) => defaultGrantCategoriesFor(id).includes(r.category)).map(
          (id) => modulePermission(id),
        ),
      ],
    ]),
  );
